import React, { useState } from 'react';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Badge } from '../../ui/Badge';
import { SearchIcon, PlusIcon, ShieldIcon, ShieldCheckIcon, EyeIcon, EditIcon, UserIcon, MoreVerticalIcon, CheckCircleIcon, XCircleIcon, ClockIcon, MailIcon, ChevronDownIcon, PencilIcon, BanIcon } from 'lucide-react';
import { AdminPortalUser, AdminRole, AdminUserStatus, ADMIN_ROLES } from '../../../types/adminUsers';
interface AdminUsersManagementProps {
  onBack: () => void;
}
export function AdminUsersManagement({
  onBack
}: AdminUsersManagementProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState<AdminRole | 'all'>('all');
  const [selectedStatus, setSelectedStatus] = useState<AdminUserStatus | 'all'>('all');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState<string | null>(null);
  const [userToUpdate, setUserToUpdate] = useState<AdminPortalUser | null>(null);
  const [inviteRole, setInviteRole] = useState<AdminRole>('admin');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [updateRole, setUpdateRole] = useState<AdminRole>('admin');
  const [updateName, setUpdateName] = useState('');
  const [updateEvents, setUpdateEvents] = useState<string[]>([]);
  // Mock events data
  const availableEvents = [{
    id: 'muscle-active-2026',
    name: 'Dubai Muscle / Active 2026'
  }, {
    id: 'fitness-expo-2026',
    name: 'Fitness Expo 2026'
  }, {
    id: 'wellness-summit-2026',
    name: 'Health & Wellness Summit 2026'
  }];
  // Mock data - in production this would come from API
  const [adminUsers, setAdminUsers] = useState<AdminPortalUser[]>([{
    id: '1',
    email: 'admin@showpilot.com',
    name: 'John Smith',
    role: 'super-admin',
    status: 'active',
    createdAt: '2024-01-01',
    lastLogin: '2025-01-20'
  }, {
    id: '2',
    email: 'sarah.jones@showpilot.com',
    name: 'Sarah Jones',
    role: 'admin',
    status: 'active',
    eventAccess: ['muscle-active-2026'],
    createdAt: '2024-06-15',
    lastLogin: '2025-01-19',
    invitedBy: 'John Smith'
  }, {
    id: '3',
    email: 'editor@showpilot.com',
    name: 'Michael Brown',
    role: 'content-editor',
    status: 'active',
    eventAccess: ['muscle-active-2026', 'fitness-expo-2026'],
    createdAt: '2024-09-10',
    lastLogin: '2025-01-18',
    invitedBy: 'John Smith'
  }, {
    id: '4',
    email: 'viewer@showpilot.com',
    name: 'Emma Wilson',
    role: 'viewer',
    status: 'active',
    eventAccess: ['muscle-active-2026'],
    createdAt: '2025-01-05',
    lastLogin: '2025-01-17',
    invitedBy: 'Sarah Jones'
  }, {
    id: '5',
    email: 'pending@showpilot.com',
    name: 'David Lee',
    role: 'content-editor',
    status: 'pending',
    eventAccess: ['fitness-expo-2026'],
    createdAt: '2025-01-20',
    invitedBy: 'John Smith'
  }]);
  const filteredUsers = adminUsers.filter(user => {
    const matchesSearch = searchQuery === '' || user.name.toLowerCase().includes(searchQuery.toLowerCase()) || user.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = selectedRole === 'all' || user.role === selectedRole;
    const matchesStatus = selectedStatus === 'all' || user.status === selectedStatus;
    return matchesSearch && matchesRole && matchesStatus;
  });
  const getRoleInfo = (role: AdminRole) => {
    return ADMIN_ROLES.find(r => r.role === role);
  };
  const getRoleIcon = (role: AdminRole) => {
    switch (role) {
      case 'super-admin':
        return <ShieldCheckIcon className="w-5 h-5" data-id="element-196" />;
      case 'admin':
        return <ShieldIcon className="w-5 h-5" data-id="element-197" />;
      case 'content-editor':
        return <EditIcon className="w-5 h-5" data-id="element-198" />;
      case 'viewer':
        return <EyeIcon className="w-5 h-5" data-id="element-199" />;
    }
  };
  const getRoleBadgeVariant = (role: AdminRole) => {
    switch (role) {
      case 'super-admin':
        return 'error';
      case 'admin':
        return 'success';
      case 'content-editor':
        return 'warning';
      case 'viewer':
        return 'default';
    }
  };
  const getStatusBadgeVariant = (status: AdminUserStatus) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'suspended':
        return 'error';
    }
  };
  const toggleEventSelection = (eventId: string) => {
    setSelectedEvents(prev => prev.includes(eventId) ? prev.filter(id => id !== eventId) : [...prev, eventId]);
  };
  const toggleUpdateEventSelection = (eventId: string) => {
    setUpdateEvents(prev => prev.includes(eventId) ? prev.filter(id => id !== eventId) : [...prev, eventId]);
  };
  const handleInviteModalOpen = () => {
    setInviteRole('admin');
    setInviteEmail('');
    setInviteName('');
    setSelectedEvents([]);
    setShowInviteModal(true);
  };
  const handleSendInvitation = () => {
    const newUser: AdminPortalUser = {
      id: String(adminUsers.length + 1),
      email: inviteEmail,
      name: inviteName,
      role: inviteRole,
      status: 'pending',
      eventAccess: inviteRole === 'super-admin' ? undefined : selectedEvents,
      createdAt: new Date().toISOString().split('T')[0],
      invitedBy: 'John Smith'
    };
    setAdminUsers([...adminUsers, newUser]);
    setShowInviteModal(false);
    setInviteEmail('');
    setInviteName('');
    setSelectedEvents([]);
  };
  const handleUpdateUser = (user: AdminPortalUser) => {
    setUserToUpdate(user);
    setUpdateRole(user.role);
    setUpdateName(user.name);
    setUpdateEvents(user.eventAccess || []);
    setShowUpdateModal(true);
    setShowActionsMenu(null);
  };
  const handleSaveUpdate = () => {
    if (!userToUpdate) return;
    setAdminUsers(adminUsers.map(user => user.id === userToUpdate.id ? {
      ...user,
      name: updateName,
      role: updateRole,
      eventAccess: updateRole === 'super-admin' ? undefined : updateEvents
    } : user));
    setShowUpdateModal(false);
    setUserToUpdate(null);
    setUpdateEvents([]);
  };
  const handleSuspendUser = (userId: string) => {
    setAdminUsers(adminUsers.map(user => user.id === userId ? {
      ...user,
      status: user.status === 'suspended' ? 'active' : 'suspended'
    } : user));
    setShowActionsMenu(null);
  };
  return <div className="space-y-6" data-id="element-200">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-201">
        <div data-id="element-202">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-203">Admin Users</h2>
          <p className="text-gray-500 mt-1" data-id="element-204">
            Manage admin portal access and permissions
          </p>
        </div>
        <Button variant="primary" onClick={handleInviteModalOpen} data-id="element-205">
          <PlusIcon className="w-4 h-4 mr-2" data-id="element-206" />
          Invite Admin User
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-207">
        <Card padding="md" data-id="element-208">
          <div className="flex items-center justify-between" data-id="element-209">
            <div data-id="element-210">
              <p className="text-sm text-gray-500" data-id="element-211">Total Admins</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-212">
                {adminUsers.length}
              </p>
            </div>
            <UserIcon className="w-8 h-8 text-blue-500" data-id="element-213" />
          </div>
        </Card>
        <Card padding="md" data-id="element-214">
          <div className="flex items-center justify-between" data-id="element-215">
            <div data-id="element-216">
              <p className="text-sm text-gray-500" data-id="element-217">Super Admins</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-218">
                {adminUsers.filter(u => u.role === 'super-admin').length}
              </p>
            </div>
            <ShieldCheckIcon className="w-8 h-8 text-red-500" data-id="element-219" />
          </div>
        </Card>
        <Card padding="md" data-id="element-220">
          <div className="flex items-center justify-between" data-id="element-221">
            <div data-id="element-222">
              <p className="text-sm text-gray-500" data-id="element-223">Admins</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-224">
                {adminUsers.filter(u => u.role === 'admin').length}
              </p>
            </div>
            <ShieldIcon className="w-8 h-8 text-green-500" data-id="element-225" />
          </div>
        </Card>
        <Card padding="md" data-id="element-226">
          <div className="flex items-center justify-between" data-id="element-227">
            <div data-id="element-228">
              <p className="text-sm text-gray-500" data-id="element-229">Editors & Viewers</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-230">
                {adminUsers.filter(u => u.role === 'content-editor' || u.role === 'viewer').length}
              </p>
            </div>
            <EditIcon className="w-8 h-8 text-orange-500" data-id="element-231" />
          </div>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card padding="md" data-id="element-232">
        <div className="flex flex-col md:flex-row gap-4" data-id="element-233">
          <div className="flex-1" data-id="element-234">
            <div className="relative" data-id="element-235">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-236" />
              <Input type="text" placeholder="Search by name or email..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 w-full" data-id="element-237" />
            </div>
          </div>
          <div className="flex gap-2" data-id="element-238">
            <select value={selectedRole} onChange={e => setSelectedRole(e.target.value as AdminRole | 'all')} className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-239">
              <option value="all" data-id="element-240">All Roles</option>
              <option value="super-admin" data-id="element-241">Super Admin</option>
              <option value="admin" data-id="element-242">Admin</option>
              <option value="content-editor" data-id="element-243">Content Editor</option>
              <option value="viewer" data-id="element-244">Viewer</option>
            </select>
            <select value={selectedStatus} onChange={e => setSelectedStatus(e.target.value as AdminUserStatus | 'all')} className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-245">
              <option value="all" data-id="element-246">All Status</option>
              <option value="active" data-id="element-247">Active</option>
              <option value="pending" data-id="element-248">Pending</option>
              <option value="suspended" data-id="element-249">Suspended</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Users Table */}
      <Card padding="none" data-id="element-250">
        <div className="overflow-x-auto" data-id="element-251">
          <table className="w-full" data-id="element-252">
            <thead className="bg-gray-50 border-b border-gray-200" data-id="element-253">
              <tr data-id="element-254">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-255">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-256">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-257">
                  Event Access
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-258">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-259">
                  Last Login
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-260">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-261">
              {filteredUsers.map(user => {
              const roleInfo = getRoleInfo(user.role);
              return <tr key={user.id} className="hover:bg-gray-50" data-id="element-262">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-263">
                      <div className="flex items-center" data-id="element-264">
                        <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center" data-id="element-265">
                          <UserIcon className="w-5 h-5 text-gray-500" data-id="element-266" />
                        </div>
                        <div className="ml-4" data-id="element-267">
                          <div className="text-sm font-medium text-gray-900" data-id="element-268">
                            {user.name}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-269">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-270">
                      <Badge variant={getRoleBadgeVariant(user.role)} data-id="element-271">
                        {roleInfo?.label}
                      </Badge>
                    </td>
                    <td className="px-6 py-4" data-id="element-272">
                      {user.role === 'super-admin' ? <span className="text-sm text-gray-900 font-medium" data-id="element-273">
                          All Events
                        </span> : user.eventAccess && user.eventAccess.length > 0 ? <div className="text-sm text-gray-900" data-id="element-274">
                          {user.eventAccess.length} event
                          {user.eventAccess.length !== 1 ? 's' : ''}
                        </div> : <span className="text-sm text-gray-400" data-id="element-275">No access</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-276">
                      <Badge variant={getStatusBadgeVariant(user.status)} data-id="element-277">
                        {user.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-278">
                      {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-279">
                      <div className="relative" data-id="element-280">
                        <button onClick={() => setShowActionsMenu(showActionsMenu === user.id ? null : user.id)} className="text-gray-400 hover:text-gray-600" data-id="element-281">
                          <MoreVerticalIcon className="w-5 h-5" data-id="element-282" />
                        </button>
                        {showActionsMenu === user.id && <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10" data-id="element-283">
                            <button onClick={() => handleUpdateUser(user)} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 border-b border-gray-100" data-id="element-284">
                              <PencilIcon className="w-4 h-4" data-id="element-285" />
                              Update User
                            </button>
                            <button onClick={() => handleSuspendUser(user.id)} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2" data-id="element-286">
                              <BanIcon className="w-4 h-4" data-id="element-287" />
                              {user.status === 'suspended' ? 'Reactivate' : 'Suspend'}{' '}
                              User
                            </button>
                          </div>}
                      </div>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Invite Modal */}
      {showInviteModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-288">
          <Card className="w-full max-w-2xl" data-id="element-289">
            <div className="p-6 max-h-[85vh] overflow-y-auto" data-id="element-290">
              <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-291">
                Invite Admin User
              </h3>
              <div className="space-y-6" data-id="element-292">
                {/* Email and Name */}
                <div className="grid grid-cols-2 gap-4" data-id="element-293">
                  <div data-id="element-294">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-295">
                      Email Address
                    </label>
                    <Input type="email" placeholder="admin@example.com" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} data-id="element-296" />
                  </div>
                  <div data-id="element-297">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-298">
                      Name
                    </label>
                    <Input type="text" placeholder="John Doe" value={inviteName} onChange={e => setInviteName(e.target.value)} data-id="element-299" />
                  </div>
                </div>

                {/* Role Selection - 2x2 Grid with Equal Heights */}
                <div data-id="element-300">
                  <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-301">
                    Select Role
                  </label>
                  <div className="grid grid-cols-2 gap-3" data-id="element-302">
                    {ADMIN_ROLES.map(role => {
                  const isSelected = inviteRole === role.role;
                  return <button key={role.role} onClick={() => setInviteRole(role.role)} className={`p-4 rounded-lg border-2 transition-all text-left h-24 ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 bg-white'}`} data-id="element-303">
                          <div className="flex items-start gap-3 h-full" data-id="element-304">
                            <div className={`flex-shrink-0 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-305">
                              {getRoleIcon(role.role)}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-306">
                              <div className={`font-semibold text-sm mb-1 ${isSelected ? 'text-blue-900' : 'text-gray-900'}`} data-id="element-307">
                                {role.label}
                              </div>
                              <div className={`text-xs leading-tight ${isSelected ? 'text-blue-700' : 'text-gray-500'}`} data-id="element-308">
                                {role.description}
                              </div>
                            </div>
                          </div>
                        </button>;
                })}
                  </div>
                </div>

                {/* Event Access - Checkbox style like Update User */}
                {inviteRole !== 'super-admin' && <div data-id="element-309">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-310">
                      Event Access
                    </label>
                    <div className="text-xs text-gray-500 mb-3" data-id="element-311">
                      Select which events this user can access
                    </div>
                    <div className="space-y-2 border border-gray-200 rounded-lg p-3" data-id="element-312">
                      {availableEvents.map(event => <label key={event.id} className="flex items-center gap-3 cursor-pointer" data-id="element-313">
                          <input type="checkbox" checked={selectedEvents.includes(event.id)} onChange={() => toggleEventSelection(event.id)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-314" />
                          <span className="text-sm text-gray-900" data-id="element-315">
                            {event.name}
                          </span>
                        </label>)}
                    </div>
                  </div>}
              </div>
              <div className="flex gap-3 mt-8" data-id="element-316">
                <Button variant="outline" onClick={() => {
              setShowInviteModal(false);
            }} className="flex-1" data-id="element-317">
                  Cancel
                </Button>
                <Button variant="primary" className="flex-1" onClick={handleSendInvitation} disabled={!inviteEmail || !inviteName} data-id="element-318">
                  <MailIcon className="w-4 h-4 mr-2" data-id="element-319" />
                  Send Invitation
                </Button>
              </div>
            </div>
          </Card>
        </div>}

      {/* Update User Modal */}
      {showUpdateModal && userToUpdate && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-320">
          <Card className="w-full max-w-2xl" data-id="element-321">
            <div className="p-6 max-h-[85vh] overflow-y-auto" data-id="element-322">
              <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-323">
                Update User
              </h3>
              <div className="space-y-6" data-id="element-324">
                {/* User Info */}
                <div className="grid grid-cols-2 gap-4" data-id="element-325">
                  <div data-id="element-326">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-327">
                      Email Address
                    </label>
                    <Input type="email" value={userToUpdate.email} disabled className="bg-gray-50" data-id="element-328" />
                  </div>
                  <div data-id="element-329">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-330">
                      Name
                    </label>
                    <Input type="text" value={updateName} onChange={e => setUpdateName(e.target.value)} placeholder="John Doe" data-id="element-331" />
                  </div>
                </div>

                {/* Role Selection */}
                <div data-id="element-332">
                  <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-333">
                    Update Role
                  </label>
                  <div className="grid grid-cols-2 gap-3" data-id="element-334">
                    {ADMIN_ROLES.map(role => {
                  const isSelected = updateRole === role.role;
                  return <button key={role.role} onClick={() => setUpdateRole(role.role)} className={`p-4 rounded-lg border-2 transition-all text-left h-24 ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 bg-white'}`} data-id="element-335">
                          <div className="flex items-start gap-3 h-full" data-id="element-336">
                            <div className={`flex-shrink-0 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-337">
                              {getRoleIcon(role.role)}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-338">
                              <div className={`font-semibold text-sm mb-1 ${isSelected ? 'text-blue-900' : 'text-gray-900'}`} data-id="element-339">
                                {role.label}
                              </div>
                              <div className={`text-xs leading-tight ${isSelected ? 'text-blue-700' : 'text-gray-500'}`} data-id="element-340">
                                {role.description}
                              </div>
                            </div>
                          </div>
                        </button>;
                })}
                  </div>
                </div>

                {/* Event Access */}
                {updateRole !== 'super-admin' && <div data-id="element-341">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-342">
                      Event Access
                    </label>
                    <div className="text-xs text-gray-500 mb-3" data-id="element-343">
                      Select which events this user can access
                    </div>
                    <div className="space-y-2 border border-gray-200 rounded-lg p-3" data-id="element-344">
                      {availableEvents.map(event => <label key={event.id} className="flex items-center gap-3 cursor-pointer" data-id="element-345">
                          <input type="checkbox" checked={updateEvents.includes(event.id)} onChange={() => toggleUpdateEventSelection(event.id)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-346" />
                          <span className="text-sm text-gray-900" data-id="element-347">
                            {event.name}
                          </span>
                        </label>)}
                    </div>
                  </div>}
              </div>
              <div className="flex gap-3 mt-8" data-id="element-348">
                <Button variant="outline" onClick={() => {
              setShowUpdateModal(false);
              setUserToUpdate(null);
            }} className="flex-1" data-id="element-349">
                  Cancel
                </Button>
                <Button variant="primary" className="flex-1" onClick={handleSaveUpdate} disabled={!updateName} data-id="element-350">
                  <CheckCircleIcon className="w-4 h-4 mr-2" data-id="element-351" />
                  Save Changes
                </Button>
              </div>
            </div>
          </Card>
        </div>}
    </div>;
}